import { MedicationRequirementStatus, ParentSafePolicy, StaffPolicyRow } from '../../types';

export interface NeedsYouRow {
  key: 'registration' | 'forms' | 'policies' | 'medication';
  label: string;
  href: string;
  count: number;
}

interface Input {
  child: {
    id: string;
    status: 'PENDING_START' | 'ACTIVE' | 'LEFT';
    badges: { pendingForms: number; registrationPending: boolean };
  };
  invitations: Array<{ id: string; studentId?: string; status: string }>;
  policies: Array<ParentSafePolicy | StaffPolicyRow>;
  medications: Array<{ status: MedicationRequirementStatus }>;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The "Needs you" list on a child's home: one row per kind of thing waiting,
 * only when it applies. A LEFT child is read-only, so nothing can be waiting.
 * Registration pending is itself a pending form invitation, so it replaces the
 * forms row rather than sitting beside it.
 */
export function buildNeedsYouRows({
  child,
  invitations,
  policies,
  medications,
}: Input): NeedsYouRow[] {
  if (child.status === 'LEFT') return [];
  const rows: NeedsYouRow[] = [];
  const base = `/parent/${child.id}`;

  if (child.badges.registrationPending) {
    rows.push({
      key: 'registration',
      label: 'Finish registration',
      href: `${base}/forms`,
      count: 1,
    });
  } else {
    const pendingForms = invitations.filter(
      (inv) => inv.studentId === child.id && inv.status === 'PENDING'
    ).length;
    if (pendingForms > 0) {
      rows.push({
        key: 'forms',
        label: `${plural(pendingForms, 'form', 'forms')} to fill`,
        href: `${base}/forms`,
        count: pendingForms,
      });
    }
  }

  const policiesWaiting = policies.filter(
    (p) =>
      'outstandingForChildren' in p && p.outstandingForChildren.some((c) => c.childId === child.id)
  ).length;
  if (policiesWaiting > 0) {
    rows.push({
      key: 'policies',
      label: `${plural(policiesWaiting, 'policy', 'policies')} to confirm`,
      href: `/parent/family/policies?child=${child.id}`,
      count: policiesWaiting,
    });
  }

  const consentAsked = medications.filter(
    (m) => m.status === MedicationRequirementStatus.PENDING_CONSENT
  ).length;
  if (consentAsked > 0) {
    rows.push({
      key: 'medication',
      label: 'Medication consent asked',
      href: `${base}/medications`,
      count: consentAsked,
    });
  }
  return rows;
}
